/**
 * TranscriptionsRoot — Plugin Root component for the transcriptions kid-renderer.
 *
 * Renders the transcript segments for the media entity whose ID == blockID.
 * The media entity becomes a root node in the note3 tree via the virtual root
 * provider hook in useRoots, and TreeItem dispatches to this plugin.
 *
 * No actual block applogs (block/type, block/content) are written — the entity
 * appears as a "virtual" block that our plugin renders.
 */

import type { KidRenderingPlugin } from '../../types'
import { For, Show, createMemo, createSignal } from 'solid-js'
import { queryAndMap } from '@wovin/core'
import { useCurrentThread, useRawThread } from '../../../ui/reactive'
import { useTranscriptApplogs } from './useTranscriptApplogs'
import { useMediaEntities } from './useMediaEntities'
import { MediaHeader } from './MediaHeader'
import { TranscriptionItem } from './TranscriptionItem'
import { getSpeakerColor } from './speakerColors'
import { SegmentVM } from './SegmentVM'
import { useTranscriptionSettings } from './useTranscriptionSettings'

export const TranscriptionsRoot: KidRenderingPlugin['Root'] = (props) => {
  const thread = useCurrentThread()
  const rawThread = useRawThread()

  // The block's own entity ID IS the mediaId for segment lookups.
  const mediaIdAccessor = createMemo(() => props.blockID as string | undefined)

  const { segments, uniqueSpeakers } = useTranscriptApplogs(mediaIdAccessor)
  const { showRawApplogs } = useTranscriptionSettings()

  // ── Session title ───────────────────────────────────────────────
  // Chain: sessionMember/media → sessionMember/session → session/title
  const sessionTitle = createMemo(() => {
    const mediaId = mediaIdAccessor()
    if (!mediaId) return null
    // Find sessionMember entity linking this media
    const members = queryAndMap(rawThread, [
      { at: 'sessionMember/media', vl: mediaId },
    ], 'en') as string[]
    if (!members.length) return null
    // Find the session ID from the first sessionMember
    const sessionIds = queryAndMap(rawThread, [
      { en: members[0], at: 'sessionMember/session' },
    ], 'vl') as string[]
    if (!sessionIds.length) return null
    // Look up session/title
    const titles = queryAndMap(rawThread, [
      { en: sessionIds[0], at: 'session/title' },
    ], 'vl') as string[]
    return titles[0] || null
  })

  const [speakerFilter, setSpeakerFilter] = createSignal<string | null>(null)

  const filteredSegments = createMemo(() => {
    const filter = speakerFilter()
    const all = segments()
    if (filter) return all.filter((s) => s.speakerLabel === filter)
    return all
  })

  const isGrouped = (index: number): boolean => {
    const segs = filteredSegments()
    if (index === 0) return false
    return segs[index].speakerLabel === segs[index - 1].speakerLabel && !!segs[index].speakerLabel
  }

  const showSpeakerDivider = (index: number): boolean => {
    const segs = filteredSegments()
    if (index === 0) return false
    const cur = segs[index]
    const prev = segs[index - 1]
    if (!cur.speakerLabel && !prev.speakerLabel) return false
    return cur.speakerLabel !== prev.speakerLabel
  }

  const segmentCount = () => segments().length
  const handleEditSave = (segmentId: string, newText: string) => {
    new SegmentVM(segmentId, thread).commitEdit(newText)
  }

  return (
    <section class="flex flex-col flex-1 min-h-0">
      {/* Empty state */}
      <Show when={!props.blockID || segmentCount() === 0}>
        <div class="flex flex-col items-center justify-center h-full text-neutral-400 dark:text-neutral-500 p-8">
          <div class="text-4xl mb-3 opacity-50">🎙</div>
          <p class="text-center text-sm">
            {segmentCount() === 0
              ? 'No transcript segments for this media'
              : 'Unknown media'}
          </p>
        </div>
      </Show>

      {/* Session heading */}
      <Show when={sessionTitle()}>
        <div class="px-4 py-3 border-b border-neutral-200 dark:border-neutral-700">
          <h2 class="text-sm font-semibold text-neutral-800 dark:text-neutral-200 truncate">
            {sessionTitle()}
          </h2>
        </div>
      </Show>

      {/* Speaker filter bar */}
      <Show when={uniqueSpeakers().length > 1}>
        <div class="flex items-center gap-2 px-4 py-2 border-b border-neutral-200 dark:border-neutral-700">
          <span class="text-10px text-neutral-400 uppercase tracking-wider">Filter:</span>
          <button onClick={() => setSpeakerFilter(null)}
            class={`px-2 py-0.5 rounded-full text-xs transition-colors ${
              speakerFilter() === null
                ? 'bg-primary/10 text-primary'
                : 'text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300'
            }`}>All</button>
          <For each={uniqueSpeakers()}>
            {(label) => (
              <button onClick={() => setSpeakerFilter(speakerFilter() === label ? null : label)}
                class={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs transition-colors ${
                  speakerFilter() === label ? 'text-white' : 'text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300'
                }`}
                style={speakerFilter() === label ? { 'background-color': getSpeakerColor(label) } : {}}>
                <span class="w-2 h-2 rounded-full" style={{ 'background-color': getSpeakerColor(label) }} />
                {label}
              </button>
            )}
          </For>
        </div>
      </Show>

      {/* Transcript list */}
      <div class="flex-1 overflow-y-auto">
        <div class="py-2 space-y-0">
          <For each={filteredSegments()}>
            {(segment, index) => (
              <>
                <Show when={showSpeakerDivider(index())}>
                  <div class="flex items-center gap-2 py-2 px-4">
                    <div class="h-px flex-1" style={{ 'background-color': segment.speakerLabel ? getSpeakerColor(segment.speakerLabel) : '#ccc' }} />
                    <span class="text-10px font-medium px-1" style={{ color: segment.speakerLabel ? getSpeakerColor(segment.speakerLabel) : '#999' }}>
                      {segment.speakerLabel ?? 'unknown'}
                    </span>
                    <div class="h-px flex-1" style={{ 'background-color': segment.speakerLabel ? getSpeakerColor(segment.speakerLabel) : '#ccc' }} />
                  </div>
                </Show>
                <TranscriptionItem
                  segment={segment}
                  onEditSave={handleEditSave}
                  showSpeakerLabel={!isGrouped(index())}
                  isGrouped={isGrouped(index())}
                  hideRawApplogs={!showRawApplogs()}
                />
              </>
            )}
          </For>
        </div>
      </div>

      {/* Footer */}
      <div class="flex items-center justify-between px-4 py-2 text-xs text-neutral-400 border-t border-neutral-200 dark:border-neutral-700">
        <span>
          {segmentCount()} segment{segmentCount() !== 1 ? 's' : ''}
          <Show when={speakerFilter()}><span class="text-primary ml-1">· filtered: {speakerFilter()}</span></Show>
        </span>
      </div>
    </section>
  )
}